/**
 * Continuous ticker for the header service bar — mobile only.
 *
 * On desktop the wrapper elements are `display: contents`, so the USP blocks stay
 * direct flex children of the bar and the normal distribution settings apply.
 * On mobile (and only when the merchant picked the marquee mode) this element
 * clones the block group often enough to cover the viewport and drives a single
 * linear CSS animation, which loops seamlessly because every copy is identical.
 *
 * Respects `prefers-reduced-motion`: the ticker stays off and the bar falls back
 * to the swipeable scroll layout.
 */

const MOBILE_QUERY = '(max-width: 1399px)';
const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';
const DEFAULT_SPEED = 40; // px per second

class UspMarquee extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-usp-track]');
    this.group = this.querySelector('[data-usp-group]');

    if (!this.track || !this.group) return;

    this.mobile = matchMedia(MOBILE_QUERY);
    this.reducedMotion = matchMedia(REDUCED_MOTION_QUERY);
    this.lastWidth = null;
    this.onMediaChange = () => this.sync(true);

    this.mobile.addEventListener('change', this.onMediaChange);
    this.reducedMotion.addEventListener('change', this.onMediaChange);

    // Observe the parent, not ourselves: while inactive this element is
    // `display: contents` and therefore has no box to measure or observe.
    this.resizeObserver = new ResizeObserver(() => this.sync());
    if (this.parentElement) this.resizeObserver.observe(this.parentElement);

    this.sync(true);
  }

  disconnectedCallback() {
    this.mobile?.removeEventListener('change', this.onMediaChange);
    this.reducedMotion?.removeEventListener('change', this.onMediaChange);
    this.resizeObserver?.disconnect();
  }

  /**
   * @param {boolean} [force] - Rebuild even when the container width is unchanged.
   */
  sync(force = false) {
    if (!this.mobile.matches || this.reducedMotion.matches) {
      this.deactivate();
      return;
    }

    const width = this.parentElement?.getBoundingClientRect().width ?? 0;

    // The clones change the track width, not the parent's — so this guard is
    // enough to keep the ResizeObserver from looping.
    if (!force && width === this.lastWidth) return;

    this.lastWidth = width;
    this.activate();
  }

  deactivate() {
    this.lastWidth = null;
    this.classList.remove('is-active');
    this.removeClones();
    this.style.removeProperty('--usp-marquee-distance');
    this.style.removeProperty('--usp-marquee-duration');
  }

  removeClones() {
    this.track.querySelectorAll('[data-usp-group-clone]').forEach((node) => node.remove());
  }

  activate() {
    this.removeClones();
    this.classList.add('is-active');

    // Measure only after the class is set: until then this element is
    // `display: contents` and has no box. Reading the box forces a layout flush,
    // so the mobile flex styles are already applied here.
    const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
    const groupWidth = this.group.getBoundingClientRect().width;
    const containerWidth = this.getBoundingClientRect().width;

    if (!groupWidth || !containerWidth) {
      this.deactivate();
      return;
    }

    const distance = groupWidth + gap;
    const copies = Math.ceil(containerWidth / distance) + 1;
    const fragment = document.createDocumentFragment();

    for (let i = 0; i < copies; i++) {
      const clone = /** @type {HTMLElement} */ (this.group.cloneNode(true));

      clone.setAttribute('aria-hidden', 'true');
      clone.removeAttribute('data-usp-group');
      clone.setAttribute('data-usp-group-clone', '');

      // Keep the theme editor and the tab order pointed at the originals only.
      clone.querySelectorAll('[data-shopify-editor-block]').forEach((node) => {
        node.removeAttribute('data-shopify-editor-block');
      });
      clone.querySelectorAll('a, button').forEach((node) => {
        node.setAttribute('tabindex', '-1');
      });

      fragment.appendChild(clone);
    }

    this.track.appendChild(fragment);

    const speed = Number(this.dataset.speed) || DEFAULT_SPEED;

    this.style.setProperty('--usp-marquee-distance', `${distance}px`);
    this.style.setProperty('--usp-marquee-duration', `${(distance / speed).toFixed(2)}s`);
  }
}

if (!customElements.get('usp-marquee')) {
  customElements.define('usp-marquee', UspMarquee);
}
